import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";

// Defaults sections
import { ComplexCommission } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import SingleTiersLoader from "@/Layouts/components/Skeletons/SingleTiersLoader";
import TierItemsLayout from "@/Layouts/components/Tiers/TierItemsLayout";

// MUI
import { Grid, Stack, Typography } from "@mui/material";
import { isSingleLastValid } from "@/Common/helpers/Matrices/tiers";

export default function DepartmentOverrideNetIncome(props) {
	const {
		sectionId = "departmentOverrideNetIncome",
		content = null,
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const lastBonusError = Boolean(errors?.get("lastBonus")?.length);
	const localErrorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onModelUpdate = (updatedModel) => {
		isValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(sectionId, null, updatedModel);
		}
	};

	// States
	const [innerModel, setInnerModel] = useCallbackState(null, onModelUpdate);

	// Methods
	const handleSwitchChange = (area, value) => {
		const updated = { ...innerModel };
		updated[area] = value;
		update(updated);
	};

	const handleAddTier = () => {
		const tiers = [...innerModel.tiers];
		const lastIndex = tiers?.length ? tiers.length - 1 : 0;
		const input = tiers?.[lastIndex]?.["bonus"]?.["input"] || "Percent";

		if (tiers?.length) {
			tiers[lastIndex].to.value = 0;
		}

		tiers.push(ComplexCommission([0, "Dollar"], [1000000, "Dollar"], { commission: [0, input] }));
		wasUpdated.current = false;
		setInnerModel({ ...innerModel, tiers });
	};

	const handleDeleteTier = () => {
		const tiers = [...innerModel.tiers];
		if (tiers.length > 1) {
			tiers.splice(-1);
		}
		const lastIndex = tiers?.length ? tiers.length - 1 : 0;
		tiers[lastIndex].to.value = 1000000;
		update({ ...innerModel, tiers });
	};

	const handleUpdateInput = (area, input) => {
		const tiers = [...innerModel.tiers];
		tiers?.forEach((tier) => {
			tier[area]["input"] = input;
			tier[area]["value"] = 0;
		});
		wasUpdated.current = false;
		setInnerModel({ ...innerModel, tiers });
	};

	const handleUpdateValue = (index, area, value) => {
		const tiers = [...innerModel.tiers];
		tiers[index][area]["value"] = value ? Number(value) : 0;
		if (tiers?.[index + 1] && area === "to") {
			tiers[index + 1]["from"]["value"] = Number(value) + 0.01;
		}
		update({ ...innerModel, tiers });
	};

	const update = (updatedModel) => {
		wasUpdated.current = true;
		setInnerModel(updatedModel);
	};

	useMemo(() => {
		if (innerModel?.tiers?.length) {
			validate([
				{
					definition: {
						useKey: "pvr",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Dollar: 0.01 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerModel.tiers || [],
				},
				{
					definition: {
						useKey: "bonus",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Percent: 0 },
								},
							],
						},
						errorsMap: ["bonus"],
						message: `Incorrect bonuses definition`,
						validationType: "SIMPLE",
					},
					section: innerModel.tiers || [],
				},
				{
					definition: {
						useKey: "lastBonus",
						validationType: "CUSTOM",
						errorsMap: ["bonus"],
						message: "Last bonus must be configured",
						operators: {
							customFn: (params = null) => {
								const { tiers = [] } = params || {};
								const lastValid = isSingleLastValid(tiers, { reference: "bonus" });
								return [{ bonus: !lastValid }];
							},
							params: {
								tiers: innerModel?.tiers || [],
							},
						},
					},
					section: [{ bonus: false }],
				},
			]);
		}
	}, [innerModel, validate]);

	useEffect(() => {
		setInnerModel(clone(content));

		return () => {
			wasUpdated.current = false;
			setInnerModel(null);
			clearSectionErrors(sectionId);
		};
	}, [sectionId, content, setInnerModel, clearSectionErrors]);

	return (
		<Grid container mt={2} px={"32px"}>
			{loading ? (
				<SingleTiersLoader buttons sx={{ padding: 0 }} />
			) : (
				<Grid container item xs={12}>
					<Stack width={1} direction={"row"} justifyContent={"space-between"}>
						<Stack>
							<TierItemsLayout
								minimumRows={1}
								columnSpacing={4}
								rows={innerModel?.tiers || []}
								columns={[
									{
										_id: "pvr",
										title: "Department PVR",
										component: {
											type: "DoubleInput",
											order: ["from", "to"],
											disableCommonInputs: true,
											properties: {
												firstInput: {
													data: [
														{
															type: "Dollar",
															min: 0,
															max: 1000000,
															decimalsEditable: true,
															decimals: 2,
															disabled: true,
														},
													],
													sx: { maxWidth: "95px" },
												},
												secondInput: {
													data: [
														{
															type: "Dollar",
															min: 0,
															max: 1000000,
															decimalsEditable: true,
															decimals: 2,
														},
													],
													sx: { maxWidth: "95px" },
												},
											},
											callbacks: {
												onActionUpdate: (positions, area, value) => handleUpdateValue(positions.row, area, value),
											},
										},
									},
									{
										_id: "bonus",
										title: "Bonus",
										component: {
											type: "InputForm",
											order: ["bonus"],
											properties: {
												input: {
													data: [
														{
															type: "Percent",
															max: 100,
															min: -100,
															decimalsEditable: true,
															decimals: 2,
														},
													],
													sx: { maxWidth: "75px" },
												},
											},
											callbacks: {
												onActionUpdate: (positions, area, value) => handleUpdateValue(positions.row, area, value),
												onChangeType: (positions, area, _, input) => handleUpdateInput(area, input),
											},
										},
									},
								]}
								errors={errors}
								errorsMessages={localErrorsMessages}
								lastCommissionError={lastBonusError}
								readOnly={readOnly}
								onActionAdd={() => handleAddTier()}
								onActionDelete={() => handleDeleteTier()}
							/>
						</Stack>
						<Stack maxWidth={"180px"}>
							<Stack>
								<Typography variant="h1" component="div" className={"tiered-title"}>
									Department <br /> Income Terms
								</Typography>
							</Stack>
							<Stack>
								<CustomizedSwitches
									textRight
									textChecked={"After Chargebacks"}
									textUnChecked={"Before Chargebacks"}
									checked={innerModel?.chargeBacks || false}
									disabled={readOnly}
									onChange={(value) => handleSwitchChange("chargeBacks", value)}
									tooltip={{
										title: "Department override gross to include or exclude chargebacks.",
										placement: "top",
										key: "department-override-net-income-chargebacks-tooltip",
									}}
								/>
							</Stack>
							<Stack>
								<CustomizedSwitches
									textRight
									textChecked={"After Comm. Payouts"}
									textUnChecked={"Before Comm. Payouts"}
									checked={innerModel?.commissionPayouts || false}
									disabled={readOnly}
									onChange={(value) => handleSwitchChange("commissionPayouts", value)}
									tooltip={{
										title: "Department override gross to include or exclude commission payouts.",
										placement: "bottom",
										key: "department-override-net-income-commission-payouts-tooltip",
									}}
								/>
							</Stack>
							<Stack>
								<CustomizedSwitches
									textRight
									textChecked={"Exclude Pers. Gross Profit"}
									textUnChecked={"Include Pers. Gross Profit"}
									checked={innerModel?.personalGrossProfit || false}
									disabled={readOnly}
									onChange={(value) => handleSwitchChange("personalGrossProfit", value)}
									tooltip={{
										title: "Department override gross to include or exclude personal gross profit.",
										placement: "bottom",
										key: "department-override-net-income-commission-payouts-tooltip",
									}}
								/>
							</Stack>
						</Stack>
					</Stack>
				</Grid>
			)}
		</Grid>
	);
}
DepartmentOverrideNetIncome.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		chargeBacks: PropTypes.bool,
		commissionPayouts: PropTypes.bool,
		personalGrossProfit: PropTypes.bool,
		tiers: PropTypes.array,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
